import { apolloClient } from '@client/api/graphql/client';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  Author,
  CommunityPostsArgs,
  GetCommunityDocument,
  GetCommunityQuery,
  GetCommunityQueryVariables,
  Post,
  Reply,
} from '../../../../generated/graphql';
import _ from 'lodash';
import { communityQueryKeys } from '../constants';

export const getParams = ({
  lastId = undefined,
  direction = undefined,
}: CommunityPostsArgs) => {
  if (lastId && direction) {
    return { lastId, direction };
  }
  return {};
};

type AuthorWithOptionalMarketing = Omit<Author, 'marketing'> &
  Partial<Pick<Author, 'marketing'>>;

type ReplyWithOptionalMarketing = Omit<Reply, 'author'> & {
  author: AuthorWithOptionalMarketing;
};

type PostWithOptionalMarketing = Omit<Post, 'author' | 'replies'> & {
  author: AuthorWithOptionalMarketing;
  replies?: ReplyWithOptionalMarketing[] | null;
};

const getPosts = async (communityId: number, pageParam: CommunityPostsArgs) => {
  const response = await apolloClient.query<
    GetCommunityQuery,
    GetCommunityQueryVariables
  >({
    query: GetCommunityDocument,
    variables: {
      id: communityId,
      ...getParams(pageParam),
    },
    fetchPolicy: 'network-only',
  });
  const posts = response.data.community?.posts as
    | PostWithOptionalMarketing[]
    | undefined;
  if (!posts) return posts as Post[] | undefined;

  const withMarketing = (author: AuthorWithOptionalMarketing): Author => ({
    ...author,
    marketing: typeof author.marketing === 'boolean' ? author.marketing : null,
  });

  const mapReplies = (replies?: ReplyWithOptionalMarketing[] | null): Reply[] =>
    (replies || []).map(
      (reply) =>
        ({
          ...reply,
          author: withMarketing(reply.author),
        } as Reply),
    );

  return posts.map((post) => ({
    ...post,
    author: withMarketing(post.author),
    replies: mapReplies(post.replies),
  })) as Post[];
};

export const useCommunityPosts = (communityId: number) => {
  return useInfiniteQuery({
    queryKey: communityQueryKeys.posts(Number(communityId)),
    queryFn: async ({ pageParam = {} }) => {
      return await getPosts(communityId, pageParam);
    },
    getNextPageParam: (lastPage) => {
      const lastCommunityPost = _.last(lastPage);
      if (lastCommunityPost?.id) {
        return {
          lastId: lastCommunityPost.id,
          direction: 'olderThan',
        };
      }
    },
    keepPreviousData: true,
    refetchOnMount: true,
    enabled: typeof communityId !== null,
    refetchOnWindowFocus: false,
    refetchInterval: 30000,
  });
};
